import {
  faCheck,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '@mui/material';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import { memo, useState } from 'react';

import { TrlFormHelperText } from '»components/atoms/TrlFormHelperText';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { TrlFieldContainer } from '../TrlFieldContainer';
import { TrlLockedField } from '../TrlLockedField';
import {
  ArrowIconStyled,
  CheckIconStyled,
  FormControlStyled,
  MenuItemStyled,
  SelectStyled,
} from './TrlFormDropdown.styles';

const getArrow = (isOpen, handleIsOpen, disabled) => {
  return (
    <ArrowIconStyled
      open={isOpen}
      icon={isOpen ? faChevronUp : faChevronDown}
      onClick={() => handleIsOpen()}
      disabled={disabled}
    />
  );
};

export const TrlFormDropdown = memo(
  ({
    name,
    label,
    placeholder,
    options,
    noPaddingTop,
    onChangeValue,
    checkTouched = true,
    isHidden,
    isRequired,
    isMultipleOption,
    disabled,
    isLocked,
    warningText,
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    const theme = useTheme();

    const handleIsOpen = () => setIsOpen((prevValue) => !prevValue);

    const getTranslatedLabel = (text) => {
      if (text?.includes('TRLT_UI-'))
        return TrlFormattedMessagePlain({ id: text });
      return text;
    };

    const getSelectedOptionLabels = (selectedValue) => {
      if (Array.isArray(selectedValue)) {
        const optionsLabel = selectedValue.reduce((acc, value) => {
          const optionLabel = options.find(
            (option) => option.value === value,
          ).label;

          acc.push(getTranslatedLabel(optionLabel));

          return acc;
        }, []);

        return optionsLabel.join(', ');
      }

      const optionLabel = options.find(
        (option) => option.value === selectedValue,
      )?.label;
      return getTranslatedLabel(optionLabel);
    };

    const getLockedFieldValue = (field) => {
      if (Array.isArray(field.value))
        return field.value.map((value, index) =>
          index !== field.value.length - 1
            ? `${getTranslatedLabel(
                options.find((option) => option.value === value).label,
              )}, `
            : getTranslatedLabel(
                options.find((option) => option.value === value).label,
              ),
        );

      const optionLabel = options.find(
        (option) => option.value === field.value,
      )?.label;
      return getTranslatedLabel(optionLabel);
    };

    return (
      <Field name={name}>
        {({ field, meta, form }) => {
          const getHelperText = () => {
            if (warningText) return warningText;
            if (checkTouched) return (meta.touched && meta.error) ?? null;
            return meta.error;
          };

          const getError = () => {
            if (checkTouched) return !!(meta.touched && meta.error);
            return !!meta.error;
          };

          return (
            <FormControlStyled
              fullWidth
              isHidden={isHidden}
              disabled={disabled}
            >
              <TrlFieldContainer
                label={label}
                noPaddingTop={noPaddingTop}
                isRequired={isRequired && !disabled && !isLocked}
                disabled={disabled}
              >
                {isLocked ? (
                  <TrlLockedField
                    showAlertIcon={!!warningText}
                    value={getLockedFieldValue(field)}
                  />
                ) : (
                  <SelectStyled
                    name={name}
                    MenuProps={{
                      style: {
                        zIndex: 1600,
                      },
                      PaperProps: {
                        sx: {
                          backgroundColor: theme.palette.colors.infoBackground,
                          boxShadow: 'none',
                          borderRadius: '5px',
                        },
                      },
                    }}
                    placeholder={placeholder}
                    open={isOpen}
                    multiple={isMultipleOption}
                    {...form}
                    {...meta}
                    {...field}
                    renderValue={getSelectedOptionLabels}
                    onChange={async (e) => {
                      await field.onChange(e);
                      if (onChangeValue) onChangeValue(e);
                    }}
                    onOpen={handleIsOpen}
                    onClose={handleIsOpen}
                    IconComponent={() =>
                      getArrow(isOpen, handleIsOpen, disabled)
                    }
                    error={getError()}
                  >
                    {options.map((option) => (
                      <MenuItemStyled key={option.value} value={option.value}>
                        {getTranslatedLabel(option.label)}
                        <CheckIconStyled icon={faCheck} />
                      </MenuItemStyled>
                    ))}
                  </SelectStyled>
                )}
              </TrlFieldContainer>
              {(!!warningText ||
                (checkTouched ? meta.touched && meta.error : meta.error)) && (
                <TrlFormHelperText
                  helperType={
                    warningText ? 'warning' : meta.error ? 'error' : 'default'
                  }
                  helperText={getHelperText()}
                  marginInline="8px 5px"
                />
              )}
            </FormControlStyled>
          );
        }}
      </Field>
    );
  },
);

TrlFormDropdown.displayName = 'TrlFormDropdown';

TrlFormDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  noPaddingTop: PropTypes.bool,
  onChangeValue: PropTypes.func,
  checkTouched: PropTypes.bool,
  isHidden: PropTypes.bool,
  isRequired: PropTypes.bool,
  isMultipleOption: PropTypes.bool,
  disabled: PropTypes.bool,
  isLocked: PropTypes.bool,
  warningText: PropTypes.string,
};
